import { Title, Text, Box, Paper, Anchor, VisuallyHidden } from "@mantine/core";
import { createFileRoute } from "@tanstack/react-router";
import { useGetCustomerInfoById } from "../hooks/api";
import { useUpdateCustomerData } from "../hooks/ui";
import capitalizeFirstLetter from "../utils/capitalizeFirstLetter";
import { isFullCredentials } from "../hooks/ui/customerDataStore";
import { LoaderComponent } from "../components/loaderComponent";
import ErrorComponent from "../components/errorComponent";
import { REPLAY_BLOCK_CLASS } from "../utils/statsig";

export const Route = createFileRoute("/profile")({
  component: RouteComponent,
});

function RouteComponent() {
  const { customerData } = useUpdateCustomerData();

  const { data, isLoading, isError } = useGetCustomerInfoById({
    credentials: customerData.credentials,
    enabled: isFullCredentials(customerData.credentials),
  });

  if (isLoading) {
    return <LoaderComponent />;
  }

  if (isError || !data) {
    return <ErrorComponent />;
  }

  return (
    <Box mt={"xl"}>
      <Title
        order={1}
        size="h2"
        ff={"var(--wallet-font-family-sambla-sans)"}
        mb={"md"}
      >
        Dina uppgifter
      </Title>
      <Text>
        Nedan ser du en sammanställning av den information vi har om dig.
        Vänligen kontakta oss om du vill ändra uppgifterna eller har några
        frågor.
      </Text>
      <Paper radius="md" p="xl" mt={"xl"}>
        <Title
          order={2}
          size="h3"
          ff={"var(--wallet-font-family-sambla-sans)"}
          mb={"md"}
        >
          Personuppgifter
        </Title>
        <Title
          order={3}
          size="h6"
          style={{ color: "var(--wallet-color-sambla-group-neutrals-500)" }}
        >
          Personnummer
        </Title>
        <Text mb={"1rem"} className={REPLAY_BLOCK_CLASS}>
          {data[0]?.user.ssn}
        </Text>
        <Title
          order={3}
          size="h6"
          style={{ color: "var(--wallet-color-sambla-group-neutrals-500)" }}
        >
          Namn
        </Title>
        <Text mb={"1rem"} className={REPLAY_BLOCK_CLASS}>
          {capitalizeFirstLetter(data[0]?.user.firstName) +
            " " +
            capitalizeFirstLetter(data[0]?.user.lastName)}
        </Text>
        <Title
          order={3}
          size="h6"
          style={{ color: "var(--wallet-color-sambla-group-neutrals-500)" }}
        >
          Email
        </Title>
        <Text mb={"1rem"} className={REPLAY_BLOCK_CLASS}>
          {data[0]?.user.email}
        </Text>
        <Title
          order={3}
          size="h6"
          style={{ color: "var(--wallet-color-sambla-group-neutrals-500)" }}
        >
          Telefon
        </Title>
        <Text mb={"1rem"} className={REPLAY_BLOCK_CLASS}>
          {data[0]?.user.phone}
        </Text>
        {data[0]?.address.address1 && (
          <>
            <Title
              order={3}
              size="h6"
              style={{
                color: "var(--wallet-color-sambla-group-neutrals-500)",
              }}
            >
              Adress
            </Title>
            <Text mb={"1rem"} className={REPLAY_BLOCK_CLASS}>
              {data[0]?.address.address1 +
                " " +
                data[0]?.address.city +
                " " +
                data[0]?.address.postCode}{" "}
            </Text>
          </>
        )}
        <Title
          order={3}
          size="h6"
          style={{ color: "var(--wallet-color-sambla-group-neutrals-500)" }}
        >
          Medborgarskap
        </Title>
        <Text mb={"1rem"}>
          {JSON.parse(data?.[0]?.kyc?.citizenship ?? "[]").map(
            (citizenship: string, idx: number) => {
              return <Text key={idx}>{citizenship}</Text>;
            },
          )}
        </Text>
        {data[0]?.user.salary && (
          <>
            {" "}
            <Title
              order={3}
              size="h6"
              style={{
                color: "var(--wallet-color-sambla-group-neutrals-500)",
              }}
            >
              Månadsinkomst
            </Title>
            <Text mb={"1rem"} className={REPLAY_BLOCK_CLASS}>
              {data[0].user.salary}
            </Text>
          </>
        )}

        <Title
          order={3}
          size="h6"
          style={{ color: "var(--wallet-color-sambla-group-neutrals-500)" }}
        >
          Betalar skatt i USA
        </Title>
        <Text mb={"1rem"}>{data[0]?.kyc.isTaxLiableInUSA ? "Ja" : "Nej"}</Text>
      </Paper>
      <Paper radius="md" p="xl" mt={"xl"}>
        <Title
          order={2}
          size="h3"
          ff={"var(--wallet-font-family-sambla-sans)"}
          mb={"md"}
        >
          Fullmakt
        </Title>
        <Text mb={"lg"}>
          Fullmakten hanteras av Fullmaktskollen och ger oss rätt att ta del av
          viss information om dina försäkringar och pensioner. Informationen
          ligger till grund för din personliga rådgivning. Du kan enkelt ändra
          eller återkalla fullmakten via{" "}
          <Anchor
            href={"https://fullmaktskollen.se"}
            underline="always"
            target="_blank"
            rel="noopener noreferrer"
          >
            www.fullmaktskollen.se.
            <VisuallyHidden> (öppnas i ny flik)</VisuallyHidden>
          </Anchor>
        </Text>
        <Title
          order={3}
          size="h6"
          style={{ color: "var(--wallet-color-sambla-group-neutrals-500)" }}
        >
          Fullmaktens giltighetstid
        </Title>
        <Text mb={"1rem"}>
          {customerData?.poa.validTo
            ? `Till och med ${customerData.poa.validTo.split("T")[0]}`
            : "Tillsvidare"}
        </Text>
        <Title
          order={3}
          size="h6"
          style={{ color: "var(--wallet-color-sambla-group-neutrals-500)" }}
        >
          Fullmaktshavare
        </Title>
        <Text mb={"1rem"}>556974-8378, Sambla Group AB</Text>
        <Title
          order={3}
          size="h6"
          style={{ color: "var(--wallet-color-sambla-group-neutrals-500)" }}
        >
          Fullmaktsgivare
        </Title>
        <Text mb={"1rem"} className={REPLAY_BLOCK_CLASS}>
          {data[0]?.user.ssn +
            ", " +
            capitalizeFirstLetter(data[0]?.user.firstName) +
            " " +
            capitalizeFirstLetter(data[0]?.user.lastName)}
        </Text>
      </Paper>
    </Box>
  );
}
